/** @format */
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

import SVGMinus from '@toucan/icons/minus.svg';
import SVGPlus from '@toucan/icons/plus.svg';

import { BaseSummaryItem } from '@toucan/client/blocks/components/base-summary-item';
import { Link, LinkSize } from '@toucan/client/shared/components/link';
import { useProductUrl } from '@toucan/client/shared/hooks/use-product-url';

import { styles } from './basket-item.style';
import { BasketItemProps } from './types';

const StyledBasketItem = styled(BaseSummaryItem)`
    ${styles}
`;

export const BasketItem: FunctionComponent<BasketItemProps> = ({
    quantity,
    showQuantitySelector,
    onQuantitySelectorPlus,
    onQuantitySelectorMinus,
    onSaveForLater,
    modelSlug,
    children,
    ...props
}) => {
    const { t } = useTranslation();
    const [isQuantitySelectorDisabled, setIsQuantitySelectorDisabled] =
        useState(false);

    const { href = '', templatePath = '' } = useProductUrl(
        props.sku,
        modelSlug
    );
    const titleLink = href && templatePath ? { href, templatePath } : undefined;

    async function changeQuantity(quantityFunc?: () => Promise<void> | void) {
        if (!quantityFunc) {
            return;
        }

        setIsQuantitySelectorDisabled(true);
        try {
            await quantityFunc();
        } finally {
            setIsQuantitySelectorDisabled(false);
        }
    }

    return (
        <StyledBasketItem {...props} titleLink={titleLink}>
            {showQuantitySelector && (
                <div
                    className="quantity-controls"
                    data-testid="basket-item__quantity-controls"
                >
                    <label>{t('Quantity')}</label>
                    <Icon
                        variant={IconVariant.ACTIONABLE}
                        size={IconSize.M}
                        onClick={() => {
                            changeQuantity(onQuantitySelectorMinus);
                        }}
                        aria-label={t('Minus')}
                        disabled={isQuantitySelectorDisabled}
                    >
                        <SVGMinus />
                    </Icon>
                    <span data-testid="basket-item__quantity-amount">
                        {quantity}
                    </span>
                    <Icon
                        variant={IconVariant.ACTIONABLE}
                        size={IconSize.M}
                        onClick={() => {
                            changeQuantity(onQuantitySelectorPlus);
                        }}
                        aria-label={t('Plus')}
                        disabled={isQuantitySelectorDisabled}
                    >
                        <SVGPlus />
                    </Icon>
                </div>
            )}
            {onSaveForLater && (
                <div className="save-for-later">
                    <Link
                        data-testid="basket-item__save-for-later"
                        onClick={onSaveForLater}
                        size={LinkSize.SMALL}
                        aria-label={t('Save for later')}
                    >
                        {t('Save for later')}
                    </Link>
                </div>
            )}
            {children}
        </StyledBasketItem>
    );
};
